import React from "react";
import {
  ScoreRangeValues,
  SkillsBasic,
  SkillBasic,
} from "@mindprint-learning/api-lib";
import { FaStar, FaCircle } from "react-icons/fa6";

import Divider from "../../../atoms/Divider/Divider";

import "./Chart.scss";

const Chart = ({ data, studentName }: ChartProps) => {
  const containsHighStrength = Object.values(data).some(
    (item) => item.range === "high_strength"
  );
  const containsLowWeakness = Object.values(data).some(
    (item) => item.range === "low_weakness"
  );

  const strength = Object.values(data).filter(
    (value: SkillBasic) =>
      value.range === ScoreRangeValues.high_strength ||
      value.range === ScoreRangeValues.medium_strength
  );

  const high_expected = Object.values(data).filter(
    (value: SkillBasic) => value.range === ScoreRangeValues.high_expected
  );

  const medium_expected = Object.values(data).filter(
    (value: SkillBasic) => value.range === ScoreRangeValues.medium_expected
  );

  const low_expected = Object.values(data).filter(
    (value: SkillBasic) => value.range === ScoreRangeValues.low_expected
  );

  const weakness = Object.values(data).filter(
    (value: SkillBasic) =>
      value.range === ScoreRangeValues.medium_weakness ||
      value.range === ScoreRangeValues.low_weakness
  );

  const invalid = Object.values(data).filter(
    (value: SkillBasic) => value.range === ScoreRangeValues.invalid
  );

  const checkSpecialKey = () => {
    switch (true) {
      case containsHighStrength && containsLowWeakness:
        return "-both";
      case containsHighStrength:
        return "-highStrength";
      case containsLowWeakness:
        return "-lowWeakness";
      default:
        return null;
    }
  };
  const hasSpecialKey = checkSpecialKey();

  return (
    <div className="mp-templates-mindprintprofile--chart">
      <div className="chart-wrapper">
        <div className="row">
          {strength.length > 0 && (
            <div className="chart-box strength">
              <div className="title strength">STRENGTHS</div>
              <div className="subtitle" />
              <div className="chart-column-wrapper">
                <div className="chart-column">
                  <ChartContent skills={strength} />
                </div>
              </div>
            </div>
          )}
          <div className="chart-box expected">
            <div className="title expected">EXPECTED RANGE</div>
            <div className="chart-column-wrapper">
              <div className="chart-column high">
                <div className="subtitle high">High</div>
                <div className="chart-content high">
                  <ChartContent skills={high_expected} />
                </div>
              </div>
              <div className="chart-column medium">
                <div className="subtitle medium">Medium</div>
                <div className="chart-content medium">
                  <ChartContent skills={medium_expected} />
                </div>
              </div>
              <div className="chart-column">
                <div className="subtitle low">Low</div>
                <div className="chart-content low">
                  <ChartContent skills={low_expected} />
                </div>
              </div>
            </div>
          </div>

          <div className="chart-box weakness">
            <div className="title weakness">SKILLS TO SUPPORT</div>
            <div className="subtitle" />
            <div className="chart-column-wrapper">
              <div className="chart-column">
                <ChartContent skills={weakness} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div>
        {invalid.length > 0 && (
          <div className="invalid-key">
            {invalid
              .sort((a, b) => b.zScore - a.zScore)
              .map((item) => {
                if (item.label !== "valid") {
                  return (
                    <div className="chart-key-text" key={item.label}>
                      <b>{item.label}</b> is not shown because the results were
                      invalid.
                    </div>
                  );
                }
              })}
          </div>
        )}
        <Divider />

        <div className="key-container">
          {strength.length > 0 && (
            <div className="key-item">
              <div className="key-box strengths" />
              <div className="key-item-element-container">
                <div className="element">
                  <b>Ahead of peers</b>
                </div>
                <div className="element">Top 16% of peer group</div>
              </div>
            </div>
          )}
          <div className="key-item">
            <div className="key-box expected" />
            <div className="key-item-element-container">
              <div className="element">
                <b>Developing appropriately for peer group</b>
              </div>
              <div className="element">
                <em>High</em>: 69th - 83rd percentile
              </div>
              <div className="element">
                <em>Medium</em>: 31st - 68th percentile
              </div>
              <div className="element">
                <em>Low</em>: 17th - 30th percentile
              </div>
            </div>
          </div>
          <div className="key-item">
            <div className="key-box weakness" />
            <div className="key-item-element-container">
              <div className="element">
                <b>Developing behind peer group, may require outside support</b>
              </div>

              <div className="element">Bottom 16% of peer group</div>
            </div>
          </div>
        </div>
        {hasSpecialKey && (
          <div className={`specialKey ` + hasSpecialKey}>
            {containsHighStrength && (
              <div className="chart-key-text">
                <FaStar /> {studentName} performed in the top 5% of peer group
              </div>
            )}
            {containsLowWeakness && (
              <div className="chart-key-text">
                <FaCircle /> {studentName} performed in the bottom 5% of peer
                group
              </div>
            )}
          </div>
        )}
      </div>

      {invalid.length > 0 && (
        <div className="invalid-container">
          <div>
            Invalid scores can arise because a student misunderstood the
            instructions, had difficulty maintaining focus during the task, was
            interrupted during the task, or had technical difficulties which
            resulted in an inconsistent response pattern. Invalid scores often
            arise because the student found the task challenging or frustrating
            and worked too quickly to be carefully considering before
            responding. If an invalid score is consistent with your observations
            about where {studentName} might be having difficulty in learning, it
            should be treated as a Skill to Support. If it is inconsistent, you
            might consider re-testing on the invalid tasks after waiting 3-6
            months to avoid possible practice effects.
          </div>
        </div>
      )}
      <Divider />
    </div>
  );
};

export default Chart;

interface ChartProps {
  data: SkillsBasic;
  studentName: string;
}

const ChartContent = ({ skills }: ChartContentProps) => {
  skills.sort((a, b) => b.zScore - a.zScore);
  return (
    <div className="chart-content">
      <>
        {skills.length > 0 ? (
          <>
            {skills.map((value: SkillBasic, index) => {
              return (
                <div className="cell" key={`score-range-${index}`}>
                  {value.label}
                  {value.range === ScoreRangeValues.high_strength && (
                    <>
                      {" "}
                      <FaStar />
                    </>
                  )}
                  {value.range === ScoreRangeValues.low_weakness && (
                    <>
                      {" "}
                      <FaCircle />
                    </>
                  )}
                </div>
              );
            })}
          </>
        ) : (
          <div className="cell -none">none</div>
        )}
      </>
    </div>
  );
};

interface ChartContentProps {
  skills: Array<SkillBasic>;
}
